// A planning screen's header (D-450): the shared `PageHeader` with the
// facility switcher as its last tool. It lives beside the screens rather than
// in the layout so a screen test that stubs the layout's facility hook renders
// the real header.

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader, type PageHeaderProps } from "../../../../components/breadcrumbs";
import { Select } from "../../../../components/select";
import { usePlanningFacility } from "../planning-layout";

/**
 * A planning screen's header (D-450): the shared `PageHeader`, with the
 * facility every planning screen reads and writes as its last action, so the
 * scope sits where every other screen keeps its tools rather than on a row of
 * its own above the tabs.
 */
export function PlanningHeader({ actions, ...rest }: PageHeaderProps): ReactNode {
  return (
    <PageHeader
      {...rest}
      actions={
        <>
          {actions}
          <FacilitySwitcher />
        </>
      }
    />
  );
}

function FacilitySwitcher(): ReactNode {
  const { t } = useTranslation();
  const { facility, facilities, selectFacility } = usePlanningFacility();
  return (
    <Select
      value={facility.id}
      aria-label={t("planning.facility.label")}
      title={t("planning.facility.label")}
      className="planning-facility"
      data-testid="planning-facility"
      onChange={(e) => {
        selectFacility(e.target.value);
      }}
    >
      {facilities.map((f) => (
        <option key={f.id} value={f.id}>
          {f.name}
        </option>
      ))}
    </Select>
  );
}
